/**
 * Ground War match settings (ARCHITECTURE 3.4), carried in MatchSettings.gw and sanitised on the server.
 * Shared cannot import the spec, so the option lists below are hard-coded copies of spec.skirmish;
 * client/test/gw-spec.test.ts checks that they match.
 */
import { GW_BASE_GRADES, GW_CLASS_IDS, GW_DIFFICULTIES, GW_GRADE_MAX, type GwClassId, type GwDifficulty, type GwGrades } from './state';

export type GwVariant = 'campaign' | 'skirmish' | 'coop' | 'pvp';
export const GW_VARIANTS: readonly GwVariant[] = ['campaign', 'skirmish', 'coop', 'pvp'];
export type GwSkirmishAi = 'rusher' | 'turtle' | 'boomer' | 'balanced' | 'random';
/** spec.skirmish.maps */
export const GW_SKIRMISH_MAPS = ['gw_meadow', 'gw_stumps', 'gw_quarry', 'gw_pits'] as const;
/** spec.skirmish.aiProfiles, in spec order. */
export const GW_SKIRMISH_AI: readonly GwSkirmishAi[] = ['rusher', 'turtle', 'boomer', 'balanced', 'random'];
/** spec.skirmish.startOre.options */
export const GW_START_ORE = [300, 1000, 3000] as const;
/** spec.skirmish.maxAge.options */
export const GW_MAX_AGE = [3, 4, 5] as const;
/** spec.skirmish.startAge {min, max} */
export const GW_START_AGE_MIN = 1;
export const GW_START_AGE_MAX = 3;
/** spec.skirmish.veteran {min 0, max 5, default 0} */
export const GW_VETERAN_MAX = 5;
/** Campaign tree record limits (3.4): at most 40 nodes, each level 1..3. */
export const GW_TREE_MAX_ENTRIES = 40;
export const GW_TREE_LEVEL_MAX = 3;

export const GW_LEVEL_ID_RE = /^(r[1-5]l[1-5]|final)$/;
const MAP_RE = /^[a-z0-9_-]{1,40}$/;
const TREE_KEY_RE = /^[a-z0-9_.-]{1,32}$/;

export interface GwMatchSettings {
  variant: GwVariant;
  /** Campaign / co-op on a level: 'r1l1'..'r5l5' | 'final' (no 'gw:' prefix). */
  levelId?: string;
  /** Skirmish/coop/pvp: a spec map id (shape-checked only; setup.ts falls back to spec.skirmish.defaultMap). */
  map?: string;
  /** Skirmish/coop opponent; 'random' is resolved once by setup.ts from team 1's aiRng. */
  ai?: GwSkirmishAi;
  /** Campaign: easy|normal|hard; skirmish/coop: all four; pvp: ignored. */
  difficulty: GwDifficulty;
  startAge?: 1 | 2 | 3;
  startOre?: 300 | 1000 | 3000;
  maxAge?: 3 | 4 | 5;
  /** Skirmish/coop only: 'Use my Burrow Upgrades'. */
  useCampaignTree?: boolean;
  /** Node id -> level. Campaign always; skirmish/co-op only with useCampaignTree; never pvp. */
  tree?: Record<string, number>;
  /** Skirmish/co-op only, 0..GW_VETERAN_MAX: weapon min(5, 1 + veteran), `veteran` in the other tracks. */
  veteran?: number;
  /** Campaign (and co-op on a level) only: the host's profile.stats.gwClasses. */
  classes?: Record<string, GwGrades>;
}

export const DEFAULT_GW_SETTINGS: GwMatchSettings = {
  variant: 'skirmish', map: 'gw_meadow', ai: 'balanced', difficulty: 'normal', startAge: 1, startOre: 300, maxAge: 5, useCampaignTree: false, veteran: 0,
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v) as unknown;
  return proto === Object.prototype || proto === null;
}

function isIntIn(v: unknown, min: number, max: number): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
}

/**
 * The campaign tree record: a plain object of at most 40 entries, key /^[a-z0-9_.-]{1,32}$/, value an integer 1..3.
 * Bad entries are dropped; anything that is not a plain object (or has more than 40 keys) gives {}.
 */
export function sanitizeGwTree(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isPlainObject(raw)) return out;
  const keys = Object.keys(raw);
  if (keys.length > GW_TREE_MAX_ENTRIES) return out;
  for (const k of keys) {
    const v = raw[k];
    if (TREE_KEY_RE.test(k) && isIntIn(v, 1, GW_TREE_LEVEL_MAX)) out[k] = v;
  }
  return out;
}

/**
 * Class grades from the wire or a profile: keys in GW_CLASS_IDS (others dropped), non-object values dropped,
 * each track an integer in range (weapon 1..5, forced to 1 for the Digger; the others 0..5), a bad or missing
 * track becomes its GW_BASE_GRADES value. Keys come out in GW_CLASS_IDS order.
 */
export function sanitizeGwGrades(raw: unknown): Partial<Record<GwClassId, GwGrades>> {
  const out: Partial<Record<GwClassId, GwGrades>> = {};
  if (!isPlainObject(raw)) return out;
  for (const id of GW_CLASS_IDS) {
    if (!Object.prototype.hasOwnProperty.call(raw, id)) continue;
    const g = raw[id];
    if (!isPlainObject(g)) continue;
    const track = (name: keyof GwGrades, min: number): number => {
      const v = g[name];
      return isIntIn(v, min, GW_GRADE_MAX) ? v : GW_BASE_GRADES[name];
    };
    out[id] = {
      weapon: id === 'digger' ? GW_BASE_GRADES.weapon : track('weapon', 1),
      vitality: track('vitality', 0),
      swiftness: track('swiftness', 0),
      mending: track('mending', 0),
    };
  }
  return out;
}

/**
 * Validates Ground War settings from the wire. Returns a fresh object, or undefined when a field is invalid.
 * Dropped rather than rejected: unknown keys, bad tree entries (and a non-object tree), veteran for campaign
 * and pvp, classes without a levelId, and tree/useCampaignTree/veteran for pvp. A missing difficulty is 'normal'.
 */
export function sanitizeGwSettings(raw: unknown): GwMatchSettings | undefined {
  if (!isPlainObject(raw)) return undefined;
  const r = raw;
  if (!(GW_VARIANTS as readonly unknown[]).includes(r.variant)) return undefined;
  const variant = r.variant as GwVariant;
  const difficulty = r.difficulty === undefined ? 'normal' : r.difficulty;
  if (!(GW_DIFFICULTIES as readonly unknown[]).includes(difficulty)) return undefined;
  if (variant === 'campaign' && difficulty === 'brutal') return undefined;
  const out: GwMatchSettings = { variant, difficulty: difficulty as GwDifficulty };

  if (r.levelId !== undefined) {
    if (typeof r.levelId !== 'string' || !GW_LEVEL_ID_RE.test(r.levelId)) return undefined;
    out.levelId = r.levelId;
  }
  if (r.map !== undefined) {
    if (typeof r.map !== 'string' || !MAP_RE.test(r.map)) return undefined;
    out.map = r.map;
  }
  if (r.ai !== undefined) {
    if (!(GW_SKIRMISH_AI as readonly unknown[]).includes(r.ai)) return undefined;
    out.ai = r.ai as GwSkirmishAi;
  }
  if (r.startAge !== undefined) {
    if (!isIntIn(r.startAge, GW_START_AGE_MIN, GW_START_AGE_MAX)) return undefined;
    out.startAge = r.startAge as 1 | 2 | 3;
  }
  if (r.startOre !== undefined) {
    if (!(GW_START_ORE as readonly unknown[]).includes(r.startOre)) return undefined;
    out.startOre = r.startOre as 300 | 1000 | 3000;
  }
  if (r.maxAge !== undefined) {
    if (!(GW_MAX_AGE as readonly unknown[]).includes(r.maxAge)) return undefined;
    out.maxAge = r.maxAge as 3 | 4 | 5;
  }
  const pvp = variant === 'pvp';
  if (r.useCampaignTree !== undefined && !pvp) {
    if (typeof r.useCampaignTree !== 'boolean') return undefined;
    out.useCampaignTree = r.useCampaignTree;
  }
  if (r.tree !== undefined && !pvp && isPlainObject(r.tree)) out.tree = sanitizeGwTree(r.tree);
  if (r.veteran !== undefined && !pvp && variant !== 'campaign') {
    if (!isIntIn(r.veteran, 0, GW_VETERAN_MAX)) return undefined;
    out.veteran = r.veteran;
  }
  if (r.classes !== undefined && out.levelId !== undefined) {
    out.classes = sanitizeGwGrades(r.classes) as Record<string, GwGrades>;
  }
  return out;
}

/** Campaign needs a levelId; pvp needs exactly two players; the rest one or more. Absent settings mean the defaults. */
export function gwCanStart(gw: GwMatchSettings | undefined, connectedPlayers: number): boolean {
  const s = gw ?? DEFAULT_GW_SETTINGS;
  if (s.variant === 'campaign') return !!s.levelId && connectedPlayers >= 1;
  if (s.variant === 'pvp') return connectedPlayers === 2;
  return connectedPlayers >= 1;
}

const AI_LABEL: Record<GwSkirmishAi, string> = { rusher: 'Rusher', turtle: 'Turtle', boomer: 'Boomer', balanced: 'Balanced', random: 'Surprise' };

/** 'r2l3' -> '2-3', 'final' -> 'Final'. */
export function gwLevelLabel(levelId: string): string {
  const m = /^r([1-5])l([1-5])$/.exec(levelId);
  if (m) return `${m[1]}-${m[2]}`;
  return levelId === 'final' ? 'Final' : levelId;
}

/** Discord presence line for a Ground War match. */
export function gwPresenceText(gw: GwMatchSettings | undefined): string {
  if (!gw) return 'Ground War';
  switch (gw.variant) {
    case 'campaign':
      return gw.levelId ? `Ground War · Campaign ${gwLevelLabel(gw.levelId)}` : 'Ground War · Campaign';
    case 'skirmish':
      return `Ground War · Skirmish vs ${AI_LABEL[gw.ai ?? 'balanced']}`;
    case 'coop':
      return 'Ground War · Co-op';
    case 'pvp':
      return 'Ground War · 1v1';
  }
}
